import React, { useMemo, useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import type { OrderItem, Customer, OrderType, Table, LoyaltyReward, OrderStatus, StaffMember, Order, WeeklyBooking } from '../../types';
import { getPaidQuantityForItem, hasUnpaidItems, isActiveBuffetSession, getKitchenCompletedFloor, isLineFullyKitchenDone, hasPendingBuffetExtras as detectPendingBuffetExtras } from '../../lib/orderUtils';
import PlusIcon from '../icons/PlusIcon';
import MinusIcon from '../icons/MinusIcon';
import TrashIcon from '../icons/TrashIcon';
import UserSearchIcon from '../icons/UserSearchIcon';
import TableIcon from '../icons/TableIcon';
import GiftIcon from '../icons/GiftIcon';
import PrinterIcon from '../icons/PrinterIcon';
import RefreshIcon from '../icons/RefreshIcon';
import EditIcon from '../icons/EditIcon';
import ItemNoteModal from './ItemNoteModal';

interface OrderSummaryProps {
  orderItems: OrderItem[];
  notes: string;
  setNotes: (notes: string) => void;
  selectedServingStaff?: StaffMember | null;
  staffMembers?: StaffMember[];
  currentUser?: StaffMember | null;
  onSelectServingStaff?: (staff: StaffMember | null) => void;
  onUpdateQuantity: (itemId: number, newQuantity: number) => void;
  onRemoveItem: (index: number) => void;
  onUpdateItemNote?: (itemId: number, note: string) => void;
  onSendToKitchen: () => void;
  onUpdateOrder: () => void;
  onCancelOrder?: () => void;
  onReleaseTable?: () => void;
  onInitiatePayment: () => void;
  onClearOrder: () => void;
  onNewOrder: () => void;
  onSelectCustomer: () => void;
  onSelectTable: () => void;
  onRedeemReward: () => void;
  onCollectMealPlan?: (booking: WeeklyBooking) => void;
  onRemoveMealPlan?: () => void;
  todayMealBookings?: WeeklyBooking[];
  appliedMealBooking?: WeeklyBooking | null;
  selectedCustomer: Customer | null;
  selectedTable: Table | null;
  orderType: OrderType;
  setOrderType: (type: OrderType) => void;
  appliedReward: LoyaltyReward | null;
  onRemoveReward: () => void;
  isSent: boolean;
  isPaid: boolean;
  onPrintReceipt: () => void;
  rewards: LoyaltyReward[];
  orderStatus?: OrderStatus;
  manualDiscount?: number;
  discountRequiresApproval?: boolean;
  discountApprovedBy?: number;
  discountApprovalReason?: string;
  discountMethod?: 'manual' | 'first-table';
  firstTableDiscountAmount?: number;
  onApplyDiscount?: () => void;
  onRemoveDiscount?: () => void;
  onRemake?: () => void;
  onTransfer?: () => void;
  onMarkAsPaid?: () => void;
  onMarkPickedUp?: () => void;
  onMarkUberPickedUp?: () => void;
  onMarkUberReady?: () => void;
  onExtendBuffetTime?: () => void;
  onCompleteBuffet?: () => void;
  isCompletingBuffet?: boolean;
  /** When provided, used to derive payment badges and aggregator restrictions. */
  currentOrder?: Order;
  isManager?: boolean;
  /** @deprecated Buffet packages come from Menu Management */
  buffetPricing?: { breakfast: number; lunch: number; dinner: number };
  selectedBuffetType?: 'breakfast' | 'lunch' | 'dinner' | null;
  selectedBuffetPackageName?: string | null;
  buffetPackagePrice?: number;
  onContinueBuffet?: () => void; // For proceeding to number of people modal
  /** Open menu to add drinks/desserts on an active buffet session */
  onAddBuffetExtras?: () => void;
  /** True while staff are on the buffet extras menu (drinks/desserts) */
  buffetExtrasMenuOpen?: boolean;
  /** Percentage to add to total for Uber/DoorDash manual orders (e.g. 15 for 15%) */
  uberDoordashPriceIncreasePercent?: number;
  /** Customer name from Takeaway/Delivery/Uber/DoorDash modal (shown in heading and customer area) */
  orderCustomerDisplayName?: string | null;
  /** Draft delivery fee before order is placed (manual delivery only). */
  pendingDeliveryCharge?: number;
  onPendingDeliveryChargeChange?: (amount: number) => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ 
    orderItems, 
    notes,
    setNotes,
    onUpdateQuantity, 
    onRemoveItem,
    onUpdateItemNote,
    onSendToKitchen,
    onUpdateOrder,
    onCancelOrder,
    onReleaseTable,
    onInitiatePayment,
    onClearOrder,
    onNewOrder,
    onSelectCustomer,
    onSelectTable,
    onRedeemReward,
    appliedMealBooking,
    selectedCustomer,
    selectedTable,
    selectedServingStaff,
    staffMembers = [],
    currentUser,
    onSelectServingStaff,
    orderType,
    setOrderType,
    appliedReward,
    onRemoveReward,
    isSent,
    isPaid,
    onPrintReceipt,
    rewards,
    orderStatus,
    manualDiscount = 0,
    discountRequiresApproval = false,
    discountApprovedBy,
    discountApprovalReason,
    discountMethod,
    firstTableDiscountAmount,
    onApplyDiscount,
    onRemoveDiscount,
    onRemake,
    onTransfer,
    onMarkAsPaid,
    onMarkPickedUp,
    onMarkUberPickedUp,
    onMarkUberReady,
    onExtendBuffetTime,
    onCompleteBuffet,
    isCompletingBuffet,
    currentOrder,
    isManager,
    buffetPricing,
    selectedBuffetType,
    selectedBuffetPackageName,
    buffetPackagePrice,
    onContinueBuffet,
    onAddBuffetExtras,
    buffetExtrasMenuOpen = false,
    uberDoordashPriceIncreasePercent = 0,
    orderCustomerDisplayName = null,
    pendingDeliveryCharge = 0,
    onPendingDeliveryChargeChange,
}) => {
  const [itemNoteModal, setItemNoteModal] = useState<{ isOpen: boolean; itemId: number; itemName: string; currentNote?: string }>({
    isOpen: false,
    itemId: 0,
    itemName: '',
  });
  const specialInstructionsField = useSoftKeyboardField({
    id: 'order-notes',
    type: 'alphanumeric',
    label: 'Special Instructions',
    value: notes,
    onChange: setNotes,
  });

  const handleOpenItemNoteModal = (item: OrderItem) => {
    setItemNoteModal({
      isOpen: true,
      itemId: item.id,
      itemName: item.name,
      currentNote: item.notes,
    });
  };

  const handleSaveItemNote = (note: string) => {
    if (onUpdateItemNote) {
      onUpdateItemNote(itemNoteModal.itemId, note);
    }
  };

  // For buffet orders, total = sum of items (buffet line is price × cover; prices are GST inclusive)
  const isBuffet = orderType === 'buffet';
  const additionalItemsTotal = orderItems.reduce(
    (sum, item) => sum + (item.removed ? 0 : item.price * item.quantity),
    0
  );
  const totalBeforeDiscount = additionalItemsTotal;
  const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
  const deliveryFeeForTotal =
    orderType === 'delivery'
      ? Math.max(0, currentOrder?.deliveryCharge ?? pendingDeliveryCharge ?? 0)
      : 0;
  let total = totalBeforeDiscount - rewardDiscount - manualDiscount + deliveryFeeForTotal;
  if (orderType === 'uber-doordash' && uberDoordashPriceIncreasePercent > 0) {
    total = total * (1 + uberDoordashPriceIncreasePercent / 100);
  }
  const tax = total * (15 / 115);
  const subtotal = total - tax;
  const activeItemCount = orderItems.filter((i) => !i.removed).length;
  const buffetBasePrice: number = !isBuffet
    ? 0
    : buffetPackagePrice != null
      ? buffetPackagePrice
      : selectedBuffetType && buffetPricing
        ? buffetPricing[selectedBuffetType]
        : 0;
  const canSendOrder = (isBuffet && buffetBasePrice > 0) || (activeItemCount > 0 && (orderType !== 'dine-in' || !!selectedTable));
  const isServed = orderStatus === 'served';

  const hasPendingBuffetExtras = useMemo(() => {
    if (!isBuffet || !currentOrder) return false;
    return detectPendingBuffetExtras(orderItems, currentOrder);
  }, [isBuffet, currentOrder, orderItems]);

  const buffetSessionActive = !!(isBuffet && currentOrder && isActiveBuffetSession(currentOrder));
  const showSendBuffetExtras =
    buffetSessionActive && (buffetExtrasMenuOpen || hasPendingBuffetExtras) && hasPendingBuffetExtras;

  const isAggregatorOrder = currentOrder?.orderType === 'uber-doordash';
  const awaitingCustomerPickup =
    !!currentOrder &&
    (currentOrder.orderType === 'takeaway' || currentOrder.orderType === 'online') &&
    currentOrder.paymentStatus === 'paid' &&
    currentOrder.status !== 'served';
  const awaitingAggregatorHandoff = isAggregatorOrder && !!currentOrder && currentOrder.status !== 'served';
  const needsPosPayment =
    !!currentOrder &&
    !isAggregatorOrder &&
    !awaitingCustomerPickup &&
    (currentOrder.paymentStatus === 'unpaid' || hasUnpaidItems(currentOrder));
  const paymentBadge = useMemo(() => {
    if (!currentOrder) {
      return isPaid ? 'PAID' : activeItemCount > 0 ? 'UNPAID' : undefined;
    }
    const { payments = [], paymentStatus } = currentOrder;
    const paidAmount = payments
      .filter(p => p.paymentStatus !== 'pending')
      .reduce((sum, p) => sum + p.amount, 0);
    const totalAmount = typeof currentOrder.totalAmount === 'number' ? currentOrder.totalAmount : total;
    if (isAggregatorOrder) return 'PAID EXTERNAL';
    if (paidAmount <= 0 && paymentStatus === 'unpaid') return 'UNPAID';
    // Itemized sub-bill payments can sum to the total before EVERY line is settled (rounding).
    // Defensive: also check there are no unpaid item quantities.
    if (paidAmount + 0.01 < totalAmount || hasUnpaidItems(currentOrder)) return 'PARTIAL';
    return 'PAID';
  }, [currentOrder, isPaid, activeItemCount, total, isAggregatorOrder]);


  const ToggleButton: React.FC<{ type: OrderType, label: string }> = ({ type, label }) => (
    <button
      onClick={() => setOrderType(type)}
      className={`flex-1 py-2 text-sm font-semibold rounded-md transition-colors ${
        orderType === type ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'
      }`}
      disabled={isSent}
    >
      {label}
    </button>
  );

  return (
    <aside className="w-72 sm:w-80 md:w-96 bg-white p-2 sm:p-4 flex flex-col shadow-lg flex-shrink-0 border-l border-gray-200 min-h-0">
      <div className="flex justify-between items-center mb-2 sm:mb-3">
        <h2 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 flex items-center gap-1 sm:gap-2">
            <span className="truncate">
              {selectedTable ? `${selectedTable.name}` : orderType === 'takeaway' && orderCustomerDisplayName ? `Takeaway - ${orderCustomerDisplayName}` : orderType === 'delivery' && orderCustomerDisplayName ? `Delivery - ${orderCustomerDisplayName}` : orderType === 'uber-doordash' ? `${currentOrder?.deliveryInfo?.platform ?? 'Uber Eats / DoorDash'}${orderCustomerDisplayName ? ` - ${orderCustomerDisplayName}` : ''}` : 'Current Order'}
            </span>
            {orderStatus && (
              <span className="text-[10px] sm:text-xs uppercase bg-gray-200 text-gray-600 font-bold px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0">
                {orderStatus}
              </span>
            )}
            {paymentBadge && (
              <span
                className={`text-[10px] sm:text-xs uppercase font-bold px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0 ${
                  paymentBadge === 'PAID' || paymentBadge === 'PAID EXTERNAL'
                    ? 'bg-green-100 text-green-700'
                    : paymentBadge === 'PARTIAL'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                }`}
              >
                {paymentBadge}
              </span>
            )}
        </h2>
        <button
            onClick={isPaid ? onNewOrder : onClearOrder}
            className="text-xs sm:text-sm text-red-500 hover:text-red-700 font-bold disabled:text-gray-400 flex-shrink-0"
            disabled={!isPaid && orderItems.length === 0 && !notes && !isSent}
        >
            {isPaid ? "New" : "Clear"}
        </button>
      </div>

      {isAggregatorOrder && (
        <div className="mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200" role="alert">
          <p className="text-xs font-semibold text-amber-800">Aggregator order (Uber Eats / DoorDash)</p>
          <ul className="text-[11px] text-amber-700 mt-1 list-disc list-inside space-y-0.5">
            <li>Payment handled by platform — do not take payment at POS</li>
            <li>Do not edit prices or line items</li>
            <li>Refunds/cancels via platform or manager only</li>
          </ul>
        </div>
      )}
      
      <div className="mb-2 sm:mb-3 space-y-1.5 sm:space-y-2">
           {(orderType === 'dine-in' || orderType === 'buffet') && (
              <div className="flex gap-2">
                  <button onClick={onSelectTable} disabled={isSent} className="flex-1 flex items-center justify-center text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-4 border-2 border-dashed rounded-lg text-gray-600 hover:bg-gray-100 hover:border-pink-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <TableIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">{selectedTable ? `Selected: ${selectedTable.name}` : 'Select Table'}</span>
                  </button>
                  {selectedTable && orderItems.length === 0 && onReleaseTable && (
                      <button 
                          onClick={onReleaseTable} 
                          className="px-2 sm:px-4 py-1.5 sm:py-2 bg-gray-200 text-gray-700 text-xs sm:text-sm font-semibold rounded-lg hover:bg-gray-300 transition-colors flex-shrink-0"
                          title="Release table"
                      >
                          Release
                      </button>
                  )}
              </div>
           )}
          {(orderType === 'dine-in' || orderType === 'buffet') && onSelectServingStaff && staffMembers.length > 0 && (
              <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 block">Serving staff (waiting)</label>
                  <select
                      value={selectedServingStaff?.id ?? currentUser?.id ?? ''}
                      onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                              onSelectServingStaff(null);
                              return;
                          }
                          const id = Number(v);
                          const staff = currentUser?.id === id ? currentUser : staffMembers.find(s => s.id === id);
                          if (staff) onSelectServingStaff(staff);
                      }}
                      disabled={isSent}
                      className="w-full text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-3 border border-gray-300 rounded-lg text-gray-700 bg-white focus:ring-2 focus:ring-pink-400 focus:border-pink-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                      {currentUser ? <option key="serving-me" value={currentUser.id}>Me ({currentUser.name})</option> : <option key="serving-not-set" value="">Not set</option>}
                      {staffMembers.filter(s => !currentUser || s.id !== currentUser.id).map(s => (
                          <option key={`serving-${(s as { firestoreId?: string }).firestoreId ?? s.id}`} value={s.id}>{s.name}</option>
                      ))}
                  </select>
              </div>
          )}
          <button onClick={onSelectCustomer} disabled={isPaid} className="w-full flex items-center justify-center text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-4 border-2 border-dashed rounded-lg text-gray-600 hover:bg-gray-100 hover:border-pink-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <UserSearchIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="truncate">{orderCustomerDisplayName ? `Selected: ${orderCustomerDisplayName}` : selectedCustomer ? `Selected: ${selectedCustomer.name}` : 'Select Customer'}</span>
          </button>
          {selectedCustomer && (
              <div className="text-center mt-2">
                  <p className="text-xs text-center text-pink-600 font-semibold mb-1">Points: {selectedCustomer.loyaltyPoints}</p>
                  {!appliedReward && !isPaid && (() => {
                      const availableRewards = rewards.filter(r => selectedCustomer.loyaltyPoints >= r.pointsRequired);
                      if (availableRewards.length > 0) {
                          return (
                              <div>
                                  <button onClick={onRedeemReward} className="text-xs bg-green-100 text-green-700 font-semibold py-1 px-2 rounded-full hover:bg-green-200 flex items-center justify-center mx-auto">
                                      <GiftIcon className="w-4 h-4 mr-1"/> Redeem Reward ({availableRewards.length})
                                  </button>
                                  {availableRewards.length === 1 && (
                                      <p className="text-xs text-gray-500 mt-1">
                                          Eligible: {availableRewards[0].name}
                                      </p>
                                  )}
                                  {availableRewards.length > 1 && (
                                      <p className="text-xs text-gray-500 mt-1">
                                          {availableRewards.length} rewards available
                                      </p>
                                  )}
                              </div>
                          );
                      }
                      // Show how many points needed for next reward
                      const sortedRewards = [...rewards].sort((a, b) => a.pointsRequired - b.pointsRequired);
                      const nextReward = sortedRewards.find(r => r.pointsRequired > selectedCustomer.loyaltyPoints);
                      if (nextReward) {
                          const pointsNeeded = nextReward.pointsRequired - selectedCustomer.loyaltyPoints;
                          return (
                              <p className="text-xs text-gray-500 mt-1">
                                  {pointsNeeded} more points for {nextReward.name}
                              </p>
                          );
                      }
                      return null;
                  })()}
              </div>
          )}
      </div>

      {activeItemCount === 0 ? (
        <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">{isPaid ? "Order completed." : isSent ? "Order sent to kitchen." : "No items in order."}</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto min-h-[140px] sm:min-h-[200px]">
            <div className="space-y-0">
                {orderItems.map((item, index) => {
                    if (item.removed) return null;
                    const minCompleted = getKitchenCompletedFloor(item);
                    const paidQty = getPaidQuantityForItem(item as OrderItem);
                    const kitchenFullyDone = isLineFullyKitchenDone(item);
                    // Lock buffet package covers once the order is placed; allow editing extras while on extras menu
                    // even if covers are already paid (isPaid).
                    const isBuffetPackageLine = !!item.isBuffetPackage;
                    const buffetExtrasLineEditable =
                      buffetSessionActive &&
                      buffetExtrasMenuOpen &&
                      !isBuffetPackageLine;
                    // Lock only finished kitchen units / paid units — not sticky status=completed after a qty add.
                    const itemIsLocked = isBuffetPackageLine && isBuffet && isSent
                      ? true
                      : buffetExtrasLineEditable
                        ? !!(
                            item.served ||
                            kitchenFullyDone ||
                            (minCompleted > 0 && item.quantity <= minCompleted) ||
                            (paidQty > 0 && paidQty >= item.quantity)
                          )
                        : !!(
                            isPaid ||
                            item.served ||
                            kitchenFullyDone ||
                            (minCompleted > 0 && item.quantity <= minCompleted) ||
                            (paidQty > 0 && paidQty >= item.quantity)
                          );
                    // Floor for +/- (used to clamp decrease so paid quantities can't be removed).
                    const minQty = Math.max(minCompleted, paidQty);
                    const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
                    const itemTotal = item.price * item.quantity;
                    const unitPrice = Math.abs(item.price);
                    const hasMoreDisplayItemsAfter = orderItems.slice(index + 1).some((i) => !i.removed);
                    return (
                        <div 
                            key={item.lineId ?? `item-${index}-${item.id}`} 
                            className={`bg-white border-l-4 ${isDiscount ? 'border-green-500 bg-green-50/30' : kitchenFullyDone ? 'border-green-500 bg-green-50/30' : 'border-pink-500'} py-2 sm:py-3 px-2 sm:px-4 ${hasMoreDisplayItemsAfter ? 'border-b border-gray-100' : ''} transition-colors ${itemIsLocked ? 'opacity-60' : ''}`}
                        >
                            {/* Item Name and Total Row */}
                            <div className="flex items-start justify-between gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                                <div className="flex-1 min-w-0 pr-1 sm:pr-2">
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        {!isDiscount && (
                                            <span 
                                                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${kitchenFullyDone ? 'bg-green-500' : 'bg-yellow-500'}`} 
                                                title={item.status}
                                            ></span>
                                        )}
                                        <h3 className={`font-semibold text-sm sm:text-base break-words ${isDiscount ? 'text-green-700' : 'text-gray-900'}`}>
                                            {isDiscount && <span className="text-green-600 mr-1">-</span>}
                                            {item.name}
                                            {item.selectedMeatOption && (
                                              <span className="ml-1.5 sm:ml-2 text-[10px] sm:text-xs font-normal text-pink-600 bg-pink-100 px-1.5 sm:px-2 py-0.5 rounded">
                                                {item.selectedMeatOption}
                                              </span>
                                            )}
                                            {paidQty > 0 && !isDiscount && (() => {
                                              const lastPayer = (() => {
                                                const map = (item as OrderItem).paidQuantityByPaymentId;
                                                if (!map || !currentOrder?.payments) return undefined;
                                                const ids = Object.keys(map);
                                                if (ids.length === 0) return undefined;
                                                const lastId = ids[ids.length - 1];
                                                const pay = currentOrder.payments.find((p) => p.id === lastId);
                                                return pay?.subBillName;
                                              })();
                                              return (
                                                <span
                                                  className="ml-1.5 sm:ml-2 text-[10px] sm:text-xs font-semibold text-green-700 bg-green-100 px-1.5 sm:px-2 py-0.5 rounded"
                                                  title={lastPayer ? `Last paid by ${lastPayer}` : 'Paid via sub-bill'}
                                                >
                                                  {lastPayer ? `Paid by ${lastPayer} ` : 'Paid '}
                                                  ({paidQty}/{item.quantity})
                                                </span>
                                              );
                                            })()}
                                        </h3>
                                    </div>
                                </div>
                                
                                {/* Item Total - Right aligned (zero for removed) */}
                                <div className="flex-shrink-0">
                                    <p className={`font-semibold text-sm sm:text-base ${item.removed ? 'text-gray-400' : isDiscount ? 'text-green-600' : 'text-gray-900'}`}>
                                        {isDiscount && !item.removed ? '-' : ''}${Math.abs(itemTotal).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Quantity and Unit Price Row */}
                            {!isDiscount && !item.removed && (
                                <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        <span className="inline-flex items-center justify-center min-w-[2.5rem] sm:min-w-[3rem] px-1.5 sm:px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] sm:text-xs font-medium rounded">
                                            {item.quantity}x
                                        </span>
                                        <span className="text-[10px] sm:text-xs text-gray-500">x ${unitPrice.toFixed(2)} / Units</span>
                                    </div>
                                    
                                    {/* Action Buttons - Always visible for touch screens */}
                                    {!itemIsLocked && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {onUpdateItemNote && (
                                                <button
                                                    onClick={() => handleOpenItemNoteModal(item)}
                                                    className={`p-1.5 rounded transition-colors ${
                                                        item.notes 
                                                            ? 'text-blue-500 hover:bg-blue-50 active:bg-blue-100' 
                                                            : 'text-gray-400 hover:bg-gray-100 active:bg-gray-200'
                                                    }`}
                                                    title={item.notes ? 'Edit note' : 'Add note'}
                                                >
                                                    <EditIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    if (item.quantity - 1 < minQty) return;
                                                    onUpdateQuantity(item.id, item.quantity - 1);
                                                }}
                                                disabled={item.quantity - 1 < minQty}
                                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                title={item.quantity - 1 < minQty ? `Cannot reduce below paid/served quantity (${minQty})` : 'Decrease quantity'}
                                            >
                                                <MinusIcon className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} 
                                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded transition-colors"
                                                title="Increase quantity"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (paidQty > 0) return;
                                                    onRemoveItem(index);
                                                }}
                                                disabled={paidQty > 0}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                title={paidQty > 0 ? `Cannot remove — ${paidQty} already paid in a sub-bill` : 'Remove item'}
                                            >
                                                <TrashIcon className="w-4 h-4"/>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Notes */}
                            {item.notes && !isDiscount && (
                                <div className="mt-2 mb-1">
                                    <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded border border-blue-200 break-words">
                                        {item.notes}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
      )}
      
      <div className="mt-2 sm:mt-4 flex-shrink-0">
        <label htmlFor="orderNotes" className="block text-xs sm:text-sm font-medium text-gray-700">Special Instructions</label>
        <textarea
          id="orderNotes"
          name="orderNotes"
          rows={2}
          value={notes}
          {...specialInstructionsField}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm bg-gray-100 text-gray-900 placeholder-gray-500 disabled:bg-gray-200"
          placeholder="e.g., Peanut allergy, extra spicy..."
          disabled={isPaid}
        ></textarea>
      </div>

      <div className="border-t pt-2 mt-2 sm:pt-4 sm:mt-4 flex-shrink-0">
        <div className="flex justify-between mb-1.5 sm:mb-2 text-gray-600 text-xs sm:text-sm">
            <span>Subtotal (ex. GST)</span>
            <span>${subtotal.toFixed(2)}</span>
        </div>
        {appliedReward && (
          <div className="flex justify-between mb-1.5 sm:mb-2 text-green-600 text-xs sm:text-sm">
            <span>{appliedReward.name}</span>
            <div className="flex items-center gap-2">
              <span>-${rewardDiscount.toFixed(2)}</span>
              {!isPaid && <button onClick={onRemoveReward} className="text-red-500 hover:text-red-700 text-xs">(Remove)</button>}
            </div>
          </div>
        )}
        {appliedMealBooking && (
          <div className="flex justify-between mb-1.5 sm:mb-2 text-indigo-700 text-xs sm:text-sm">
            <span>Meal plan · {appliedMealBooking.period}</span>
            <span>Included</span>
          </div>
        )}
        {manualDiscount > 0 && (
          <div className="flex justify-between mb-1.5 sm:mb-2 text-blue-600 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <span>
                {discountMethod === 'first-table' ? 'First Table Discount' : 'Manual Discount'}
              </span>
              {discountRequiresApproval && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded" title={`Approved by Manager ID: ${discountApprovedBy}`}>
                  ✓ Approved
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span>-${manualDiscount.toFixed(2)}</span>
              {!isPaid && onRemoveDiscount && (
                <button onClick={onRemoveDiscount} className="text-red-500 hover:text-red-700 text-xs">(Remove)</button>
              )}
            </div>
          </div>
        )}
        {!isPaid && onApplyDiscount && (
          <button
            onClick={onApplyDiscount}
            data-testid="pos-apply-discount"
            className="w-full mb-1.5 sm:mb-2 py-1.5 sm:py-2 px-3 sm:px-4 bg-blue-100 text-blue-700 rounded-md font-semibold hover:bg-blue-200 transition-colors text-xs sm:text-sm"
          >
            {manualDiscount > 0 ? 'Edit Discount' : 'Apply Discount'}
          </button>
        )}
        {orderType === 'delivery' && !isPaid && !isSent && onPendingDeliveryChargeChange && (
          <div className="mb-2 p-2 rounded-lg border border-pink-200 bg-pink-50/80">
            <label htmlFor="delivery-charge-pos" className="block text-xs font-semibold text-pink-800 mb-1">
              Delivery charge (GST incl.)
            </label>
            <input
              id="delivery-charge-pos"
              type="text"
              inputMode="decimal"
              value={pendingDeliveryCharge === 0 ? '' : String(pendingDeliveryCharge)}
              onChange={(e) => {
                const raw = e.target.value.replace(',', '.');
                if (raw === '') {
                  onPendingDeliveryChargeChange(0);
                  return;
                }
                if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
                const n = parseFloat(raw);
                if (Number.isNaN(n) || n < 0) return;
                onPendingDeliveryChargeChange(Math.round(n * 100) / 100);
              }}
              placeholder="0.00"
              className="w-full px-2 py-1.5 border border-pink-300 rounded-md text-sm text-gray-900 bg-white"
            />
          </div>
        )}
        {orderType === 'delivery' && deliveryFeeForTotal > 0 && (
          <div className="flex justify-between mb-1.5 sm:mb-2 text-pink-700 text-xs sm:text-sm font-medium">
            <span>Delivery</span>
            <span>+${deliveryFeeForTotal.toFixed(2)}</span>
          </div>
        )}
         <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            <span>Taxes</span>
            <span>${tax.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-sm sm:text-base font-bold text-gray-900 pt-1.5 sm:pt-2 border-t border-gray-200 mt-1.5 sm:mt-2">
            <span>Total</span>
            <span data-testid="pos-order-total">${total.toFixed(2)}</span>
        </div>
        {orderType === 'uber-doordash' && uberDoordashPriceIncreasePercent > 0 && (
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
            Includes {uberDoordashPriceIncreasePercent}% platform price adjustment
          </p>
        )}

        {showSendBuffetExtras ? (
            <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                <button
                    type="button"
                    onClick={onUpdateOrder}
                    className="w-full bg-amber-500 text-white font-semibold py-2.5 sm:py-3 rounded-lg hover:bg-amber-600 transition-colors duration-300 flex items-center justify-center gap-1.5 text-xs sm:text-sm"
                    title="Send drinks/desserts/add-ons to kitchen"
                >
                    <RefreshIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"/>
                    <span className="truncate">Send extras to kitchen</span>
                </button>
                {onAddBuffetExtras && !buffetExtrasMenuOpen && (
                    <button
                        type="button"
                        onClick={onAddBuffetExtras}
                        className="w-full bg-pink-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-pink-600 transition-colors text-xs sm:text-sm"
                    >
                        Continue adding extras
                    </button>
                )}
            </div>
        ) : isPaid ? (
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                <button 
                    onClick={onPrintReceipt}
                    className="col-span-2 sm:col-span-1 bg-blue-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center gap-1.5 text-xs sm:text-sm"
                >
                    <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"/>
                    <span className="truncate">Print Receipt</span>
                </button>
                {onRemake && currentOrder && currentOrder.orderType !== 'buffet' && isPaid && (
                    <button 
                        onClick={onRemake}
                        className="col-span-2 sm:col-span-1 bg-purple-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-purple-600 transition-colors duration-300 text-xs sm:text-sm"
                    >
                        <span className="truncate">Remake Order</span>
                    </button>
                )}
                {buffetSessionActive && (
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2 col-span-2">
                        {onAddBuffetExtras && !buffetExtrasMenuOpen && (
                            <button
                                type="button"
                                onClick={onAddBuffetExtras}
                                className="col-span-2 bg-amber-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-amber-600 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Add extras (drinks / desserts)</span>
                            </button>
                        )}
                        {buffetExtrasMenuOpen && !hasPendingBuffetExtras && (
                            <p className="col-span-2 text-center text-xs text-gray-500 py-1">
                                Pick drinks or desserts from the menu, then send them to kitchen.
                            </p>
                        )}
                        {onCompleteBuffet && (
                            <button
                                onClick={onCompleteBuffet}
                                className="bg-purple-600 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-purple-700 transition-colors duration-300 disabled:opacity-60 text-xs sm:text-sm"
                                disabled={isCompletingBuffet}
                            >
                                <span className="truncate">{isCompletingBuffet ? 'Ending...' : 'End Buffet'}</span>
                            </button>
                        )}
                        {onExtendBuffetTime && (
                            <button
                                onClick={onExtendBuffetTime}
                                className="bg-indigo-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-indigo-600 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Extend Time</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        ) : isSent ? (
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                <button
                    onClick={onPrintReceipt}
                    className="bg-blue-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
                    title="Print order / bill to present to customer"
                >
                    <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"/>
                    <span className="truncate">Print Order</span>
                </button>
                {awaitingCustomerPickup && onMarkPickedUp ? (
                    <button
                        onClick={onMarkPickedUp}
                        className="bg-green-600 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-green-700 transition-colors duration-300 text-xs sm:text-sm"
                    >
                        <span className="truncate">Order Picked Up</span>
                    </button>
                ) : awaitingAggregatorHandoff ? (
                    <>
                        {onMarkUberReady && !currentOrder?.deliveryInfo?.uberReadySentAt && (
                            <button
                                onClick={onMarkUberReady}
                                className="bg-green-100 text-green-800 font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-green-200 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Ready for Pick Up</span>
                            </button>
                        )}
                        {onMarkUberPickedUp && (currentOrder?.deliveryInfo?.uberReadySentAt || currentOrder?.status === 'ready') && (
                            <button
                                onClick={onMarkUberPickedUp}
                                className="bg-green-600 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-green-700 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Mark Picked Up</span>
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <button
                            onClick={onUpdateOrder}
                            data-testid="pos-update-order"
                            disabled={
                              currentOrder &&
                              (currentOrder.orderType === 'takeaway' ||
                                currentOrder.orderType === 'delivery' ||
                                currentOrder.orderType === 'uber-doordash')
                            }
                            className="bg-blue-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                            title={
                              currentOrder &&
                              (currentOrder.orderType === 'takeaway' ||
                                currentOrder.orderType === 'delivery' ||
                                currentOrder.orderType === 'uber-doordash')
                                ? 'This order type cannot be updated here'
                                : isBuffet
                                  ? 'Send extras to kitchen'
                                  : 'Update order with new items'
                            }
                        >
                            <RefreshIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"/>
                            <span className="truncate">{isBuffet ? 'Send extras' : 'Update'}</span>
                        </button>
                        {needsPosPayment && (
                            <button 
                                onClick={onInitiatePayment}
                                data-testid="pos-pay-now"
                                className="bg-green-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-green-600 transition-colors duration-300 flex items-center justify-center text-xs sm:text-sm"
                                title="Open payment screen"
                            >
                                <span className="truncate">Pay Now</span>
                            </button>
                        )}
                        {onMarkAsPaid && needsPosPayment && (
                            <button
                                onClick={onMarkAsPaid}
                                className="bg-amber-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-amber-600 transition-colors duration-300 flex items-center justify-center text-xs sm:text-sm"
                                title="Use when payment was taken but order still shows unpaid (reconciliation)"
                            >
                                <span className="truncate">Mark as paid</span>
                            </button>
                        )}
                    </>
                )}
                {onCancelOrder && needsPosPayment && (
                    <button
                        onClick={onCancelOrder}
                        className="bg-red-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-red-600 transition-colors duration-300 text-xs sm:text-sm col-span-2"
                    >
                        <span className="truncate">Cancel</span>
                    </button>
                )}
                {onTransfer && currentOrder && currentOrder.orderType === 'dine-in' && currentOrder.paymentStatus === 'unpaid' && currentOrder.status !== 'comped' && (
                    <button 
                        onClick={onTransfer}
                        className="bg-orange-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-orange-600 transition-colors duration-300 text-xs sm:text-sm col-span-2"
                    >
                        <span className="truncate">Transfer</span>
                    </button>
                )}
                {buffetSessionActive && (
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2 col-span-2">
                        {onAddBuffetExtras && !buffetExtrasMenuOpen && (
                            <button
                                type="button"
                                onClick={onAddBuffetExtras}
                                className="col-span-2 bg-amber-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-amber-600 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Add extras (drinks / desserts)</span>
                            </button>
                        )}
                        {onCompleteBuffet && (
                            <button
                                onClick={onCompleteBuffet}
                                className="bg-purple-600 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-purple-700 transition-colors duration-300 disabled:opacity-60 text-xs sm:text-sm"
                                disabled={isCompletingBuffet}
                            >
                                <span className="truncate">{isCompletingBuffet ? 'Ending...' : 'End Buffet'}</span>
                            </button>
                        )}
                        {onExtendBuffetTime && (
                            <button
                                onClick={onExtendBuffetTime}
                                className="bg-indigo-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-indigo-600 transition-colors duration-300 text-xs sm:text-sm"
                            >
                                <span className="truncate">Extend Time</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        ) : (
            <>
                {/* For buffet orders, show "Continue" button to proceed to number of people */}
                {orderType === 'buffet' && selectedBuffetType && buffetBasePrice > 0 && !isPaid && onContinueBuffet && (
                    <button 
                        onClick={onContinueBuffet}
                        className="w-full bg-pink-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-pink-600 transition-colors duration-300 text-xs sm:text-sm"
                    >
                        Continue{selectedBuffetPackageName ? ` · ${selectedBuffetPackageName}` : ''} (${buffetBasePrice.toFixed(2)}/person)
                    </button>
                )}
                {/* Don't show "Send to Kitchen" for buffet orders - they go directly to payment */}
                {orderType !== 'buffet' && (
                    <button 
                        onClick={onSendToKitchen}
                        data-testid="pos-send-to-kitchen"
                        className="w-full bg-pink-500 text-white font-semibold py-2 sm:py-2.5 rounded-lg hover:bg-pink-600 transition-colors duration-300 disabled:bg-gray-400 text-xs sm:text-sm"
                        disabled={!canSendOrder}
                    >
                        Send to Kitchen
                    </button>
                )}
            </>
        )}
      </div>
      
      {itemNoteModal.isOpen && (
        <ItemNoteModal
          isOpen={itemNoteModal.isOpen}
          itemName={itemNoteModal.itemName}
          currentNote={itemNoteModal.currentNote}
          onClose={() => setItemNoteModal({ isOpen: false, itemId: 0, itemName: '' })}
          onSave={handleSaveItemNote}
        />
      )}
    </aside>
  );
};

export default OrderSummary;
